<template>
  <div class="category-management" dir="rtl">
    <div class="container">
      <!-- Header Section -->
      <header class="header-section">
        <h1 class="page-title">إدارة الكوبونات</h1>
        <p class="page-subtitle">قم بإدارة كوبونات الخصم بسهولة وفعالية</p>
        <button class="btn-add" @click="openModalForAdd">
          <span class="btn-icon">+</span>
          إضافة كوبون جديد
        </button>
      </header>

      <!-- Modal -->
      <transition name="modal">
        <div v-if="showCouponModal" class="modal-overlay" @click.self="closeCouponModal">
          <div class="modal-content">
            <div class="modal-header">
              <h2 class="modal-title">
                {{ isEditMode ? "تعديل الكوبون" : "إضافة كوبون جديد" }}
              </h2>
              <button class="modal-close" @click="closeCouponModal">&times;</button>
            </div>
            
            <form @submit.prevent="handleSubmit" class="coupon-form">
              <div class="form-group">
                <label for="code" class="form-label">كود الكوبون</label>
                <div class="input-group">
                  <input
                    type="text"
                    id="code"
                    v-model="form.code"
                    class="form-control"
                    placeholder="أدخل كود الكوبون"
                    required
                  />
                  <button type="button" class="btn-generate" @click="generateCouponCode">
                    توليد
                  </button>
                </div>
              </div>

              <div class="form-group">
                <label for="discountPercentage" class="form-label">النقاط </label>
                <input
                  type="number"
                  id="discountPercentage"
                  v-model.number="form.discountPercentage"
                  class="form-control"
                  placeholder="أدخل النقاط"
                  min="0"
                  max="10000000"
                  required
                />
              </div>

    
                <div class="form-group ">
                  <label for="validFrom" class="form-label">ساري من</label>
                  <input
                    type="datetime-local"
                    id="validFrom"
                    v-model="form.validFrom"
                    class="form-control"
                    required
                  />
                </div>

                <div class="form-group">
                  <label for="validTo" class="form-label">ساري إلى</label>
                  <input
                    type="datetime-local"
                    id="validTo"
                    v-model="form.validTo"
                    class="form-control"
                    required
                  />
                </div>
       
              <div class="modal-actions">
                <button type="submit" class="btn-submit" :disabled="isSubmitting">
                  <span v-if="isSubmitting" class="spinner"></span>
                  {{ isEditMode ? "تحديث الكوبون" : "إضافة الكوبون" }}
                </button>
                <button type="button" class="btn-cancel" @click="closeCouponModal">
                  إلغاء
                </button>
              </div>
            </form>
          </div>
        </div>
      </transition>

      <!-- Table Section -->
      <div class="table-section">
        <div class="table-header">
          <h2 class="table-title">قائمة الكوبونات</h2>
          <div class="table-actions">
            <div class="search-container">
              <input 
                type="text" 
                placeholder="بحث..." 
                class="search-input"
                v-model="searchQuery"
              />
              <span class="search-icon">🔍</span>
            </div>
          </div>
        </div>

        <div class="table-container">
          <table class="categories-table">
            <thead>
              <tr>
                <th>التسلسل</th>
                <th>الكود</th>
                <th>النقاط</th>
                <th>ساري من</th>
                <th>ساري إلى</th>
                <th>الحالة</th>
                <th>عدد الاستخدام</th>
                <th>الإجراءات</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(coupon, index) in filteredCoupons" 
                  :key="coupon.id"
                  class="table-row"
                  :class="{ 'row-fade': coupon.isDeleting }"
              >
                <td>{{ (pageNumber - 1) * pageSize + (index + 1) }}</td>
                <td>
                  <span class="coupon-code">{{ coupon.code }}</span>
                </td>
                <td>{{ coupon.discountPercentage }}</td>
                <td>{{ formatDate(coupon.validFrom) }}</td>
                <td>{{ formatDate(coupon.validTo) }}</td>
                <td>
                  <span class="status-badge" :class="getCouponStatus(coupon)">
                    {{ getCouponStatusText(coupon) }}
                  </span>
                </td>
                <td>{{ coupon.usedCount }}</td>
                <td class="actions-cell">
                  <button class="btn-action edit" @click="openModalForEdit(coupon)">
                    <span class="action-icon">✎</span>
                    تعديل
                  </button>
                  <button class="btn-action delete" @click="deleteCoupon(coupon.id)">
                    <span class="action-icon">×</span>
                    حذف
                  </button>
                </td>
              </tr>
              <tr v-if="filteredCoupons.length === 0">
                <td colspan="8" class="empty-state">
                  لا توجد كوبونات متاحة
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div class="pagination-container">
          <custom-pagination
            :currentPage="pageNumber"
            :totalPages="totalPages"
            @page-changed="handlePageChange"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from "@/api";
import CustomPagination from "@/components/CustomPagination.vue";
import Swal from "sweetalert2";

export default {
  name: "CouponManagement",
  components: { CustomPagination },
  data() {
    return {
      coupons: [],
      pageNumber: 1,
      pageSize: 8,
      totalPages: 1,
      showCouponModal: false,
      isEditMode: false,
      isSubmitting: false,
      searchQuery: "",
      form: {
        id: null,
        code: "",
        discountPercentage: 0,
        validFrom: "",
        validTo: "",
      },
    };
  },
  computed: {
    filteredCoupons() {
      if (!this.searchQuery) return this.coupons;
      const query = this.searchQuery.toLowerCase();
      return this.coupons.filter(coupon => 
        coupon.code.toLowerCase().includes(query) ||
        coupon.discountPercentage.toString().includes(query)
      );
    }
  },
  methods: {
    // openModalForAdd

    openModalForAdd() {
      this.isEditMode = false;
      this.generateCouponCode();
      this.showCouponModal = true;
    },



    // openModalForEdit

    openModalForEdit(coupon) {
      this.isEditMode = true;
      this.form = {
        id: coupon.id,
        code: coupon.code,
        discountPercentage: coupon.discountPercentage,
        validFrom: coupon.validFrom,
        validTo: coupon.validTo,
      };
      this.showCouponModal = true;
    },

    async getCoupons() {
      try {
        const response = await apiClient.get("/api/Coupon/GetCoupons", {
          params: {
            PageNumber: this.pageNumber,
            PageSize: this.pageSize,
          },
        });

        const d = response.data;
        this.coupons = d.items ?? [];
        this.totalPages = Math.max(1, Number(d.totalPages) || 1);
        // لا نستبدل pageNumber من الاستجابة: إن كان الـ API يعيد دائماً 1 يُعيد التحديد إلى الصفحة الأولى
        if (this.pageNumber > this.totalPages) {
          this.pageNumber = this.totalPages;
        }
      } catch (error) {
        this.showError("حدث خطأ أثناء جلب الكوبونات");
      }
    },

    handlePageChange(newPage) {
      this.pageNumber = newPage;
      this.getCoupons();
    },

    generateCouponCode() {
      const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
      let code = '';
      for (let i = 0; i < 8; i++) {
        code += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      this.form.code = code;
    },

    getCouponStatus(coupon) {
      const now = new Date();
      const validFrom = new Date(coupon.validFrom);
      const validTo = new Date(coupon.validTo);
      
      if (now < validFrom) return 'status-pending';
      if (now > validTo) return 'status-expired';
      return 'status-active';
    },

    getCouponStatusText(coupon) {
      const now = new Date();
      const validFrom = new Date(coupon.validFrom);
      const validTo = new Date(coupon.validTo);
      
      if (now < validFrom) return 'قريباً';
      if (now > validTo) return 'منتهي';
      return 'نشط';
    },

    formatDate(dateStr) {
      if (!dateStr) return "";
      const date = new Date(dateStr);
      return date.toLocaleDateString('ar-EG', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    async handleSubmit() {
      this.isSubmitting = true;
      try {
        const couponData = {
          code: this.form.code,
          discountPercentage: this.form.discountPercentage,
          validFrom: new Date(this.form.validFrom).toISOString(),
          validTo: new Date(this.form.validTo).toISOString(),
        };

        if (this.isEditMode) {
          await apiClient.put("/api/Coupon/UpdateCoupon", couponData, {
            params: { id: this.form.id },
          });
          this.showSuccess("تم تحديث الكوبون بنجاح");
        } else {
          await apiClient.post("/api/Coupon/AddCoupon", couponData);
          this.showSuccess("تم إضافة الكوبون بنجاح");
        }

        this.closeCouponModal();
        this.getCoupons();
      } catch (error) {
        this.showError("حدث خطأ أثناء معالجة العملية");
      } finally {
        this.isSubmitting = false;
      }
    },

    showSuccess(message) {
      Swal.fire({
        icon: "success",
        title: "تم",
        text: message,
        timer: 2000,
        showConfirmButton: false,
        customClass: {
          popup: 'success-modal'
        }
      });
    },
    
    // deleteCoupon 
    async deleteCoupon(id) {
      const result = await Swal.fire({
        title: "تأكيد الحذف",
        text: "هل أنت متأكد من حذف هذا الكوبون؟",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#dc3545",
        cancelButtonColor: "#6c757d",
        confirmButtonText: "نعم، احذفه",
        cancelButtonText: "إلغاء",
      });

      if (result.isConfirmed) {
        try {
          await apiClient.delete("/api/Coupon/DeleteCoupon", {
            params: { id },
          });
          Swal.fire({
            icon: "success",
            title: "تم الحذف",
            text: "تم حذف الكوبون بنجاح",
            timer: 2000,
            showConfirmButton: false,
          });
          this.getCoupons();
        } catch (error) {
          console.error("حدث خطأ أثناء حذف الكوبون:", error);
          Swal.fire({
            icon: "error",
            title: "خطأ",
            text: "حدث خطأ أثناء حذف الكوبون",
            timer: 3000,
            showConfirmButton: false,
          });
        }
      }
    },
    

    closeCouponModal() {
      this.showCouponModal = false;
      this.isEditMode = false;
      this.form = {
        id: null,
        code: "",
        discountPercentage: 0,
        validFrom: "",
        validTo: "",
      };
    },

    showError(message) {
      Swal.fire({
        icon: "error",
        title: "خطأ",
        text: message,
        timer: 3000,
        showConfirmButton: false,
        customClass: {
          popup: 'error-modal'
        }
      });
    },

    // ... باقي الأساليب (methods) تبقى كما هي
  },
  mounted() {
    this.getCoupons();
  },
};
</script>

<style scoped>
/* استخدم نفس التصميم السابق مع إضافة الأنماط الخاصة بالكوبونات */

.coupon-code {
  font-family: monospace;
  background: #f8fafc;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-weight: 600;
  letter-spacing: 1px;
}

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 1rem;
  font-size: 0.875rem;
  font-weight: 500;
}

.modal-content {
  background-color: white;
  border-radius: 0.5rem;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  width: 100%;
  max-width: 500px;
}

.status-active {
  background-color: #dcfce7;
  color: #166534;
}

.status-pending {
  background-color: #fef9c3;
  color: #854d0e;
}

.status-expired {
  background-color: #fee2e2;
  color: #991b1b;
}

.input-group {
  display: flex;
  gap: 0.5rem;
}

.btn-generate {
  padding: 0.5rem 1rem;
  background-color: #4b5563;
  color: white;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-generate:hover {
  background-color: #374151;
}

.form-row {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
}

.flex-1 {
  flex: 1;
}

/* تضمين باقي الأنماط من التصميم السابق */
</style>